import useModalClass from 'hooks/useModalClass';
import { FC, Fragment, useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getStatus } from 'constants/mo';
import Button from 'components/ui/Button/Button';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import FormGroup from 'components/ui/FormGroup/FormGroup';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import { FullSegment, OrderRow } from 'ts/shared/interfaces/MOResponse';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faTicket, faTimes } from '@fortawesome/free-solid-svg-icons';
import { showError, showSuccess } from 'utils/utils';
import { validate } from 'utils/validation';
import { getVoucher, orderToEmail } from 'services/myOrders';
import ScreenshotSupport from '../../ScreenshotSupport';
import MaximizeModal from '../../MaximizeModal';
import withErrorBoundary from '../../withErrorBoundary';
import { createPortal } from 'react-dom';
import useDraggableModal from 'hooks/useDraggableModal';
import { MainContext } from 'contexts/MainContext';

interface MoEmailModalProps {
  onClose: () => void;
  isOpen: boolean;
  orderRow: OrderRow;
  reportType: string;
  roomids?: string;
  thinVoucher?: string;
  showThinVoucher?: boolean;
  segment?: FullSegment;
}

const MoEmailModal: FC<MoEmailModalProps> = ({ orderRow, ...props }) => {
  const { t } = useTranslation();
  const modalOverlayRef = useRef<HTMLDivElement>(null);
  const classList = useModalClass(props.isOpen);
  const { modalRef, modalStyle, dragHandleProps, isDragging } = useDraggableModal(true);
  const [toMe, setToMe] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [thinReport, setThinReport] = useState(props.thinVoucher || '0');
  const { user } = useContext(MainContext);
  const [email, setEmail] = useState(user?.email || '');

  const [isPrinting, setIsPrinting] = useState(false);

  const onClickSumit = async () => {
    const isValid = validate({ email: { required: !toMe, email: true } }, { email });
    setIsSubmitted(true);

    if (isValid) {
      props.onClose();

      const res = await orderToEmail({
        orderId: orderRow.orderId,
        segmentId: orderRow.segmentId,
        email,
        sendEmailToMe: toMe,
        reportType: props.reportType,
        roomids: props.roomids,
        teamVoucher: thinReport === '1',
      });

      if (res?.success) {
        showSuccess(t('mo.email_success'));
      }
    }
  };
  const onClickPrint = async () => {
    setIsPrinting(true);
    if (props.segment) {
      const res = await getVoucher(
        props.segment,
        props.segment.backOfficeExchangeVoucher,
        thinReport === '1'
      );

      if (res) {
        showSuccess(t('mo.download_success'));
      } else {
        showError(t('something_went_wrong'));
      }
    }
    setIsPrinting(false);
  };

  return (
    <Fragment>
      {props.isOpen && createPortal(
        <div
          className="modal-overlay modal-overlay-top modal-maximizable" style={{ direction: 'ltr' }}
          ref={modalOverlayRef}
          onClick={(e) => e.target === e.currentTarget && props.onClose()}
        >
          <div ref={modalRef} className={classList} style={modalStyle}>
            <div
              className={'modal-title draggable-modal-handle' + (isDragging ? ' dragging' : '')}
              {...dragHandleProps}
            >
              <div>
                <span className="title mr-10">
                  {t('mo.order_report')}{' '}
                  {' '}-{' '}Order: {orderRow.orderId}{' '}
                  Segment: {orderRow.segmentId}{' '}
                  {!!orderRow.status && (
                    <span className={`mo-status ${getStatus(orderRow.status)}`}>
                      {orderRow.status}
                    </span>
                  )}
                </span>
              </div>
              <div data-no-drag="true">
                <ScreenshotSupport />
                <MaximizeModal modalOverlayRef={modalOverlayRef} />
                <FontAwesomeIcon icon={faTimes} onClick={() => props.onClose()} />
              </div>
            </div>
            <div className="modal-content">
              <FormGroup
                className="mb-20"
                label={''}
                type="email"
                movablePlaceholder={t('passenger.email')}
                movablePlaceholderIcon={faEnvelope}
                value={email}
                onChange={setEmail}
                validation={{ required: !toMe, email: true }}
                isSubmitted={isSubmitted}
              />

              {props.showThinVoucher && (
                <Fragment>
                  <div className="mb-20"></div>
                  <SelectCustom
                    options={[
                      { value: '0', label: t('mo.regular_voucher') },
                      { value: '1', label: t('mo.thin_voucher') },
                    ]}
                    value={thinReport}
                    onChange={(value) => setThinReport(value)}
                    movablePlaceholder={t('mo.voucher_type')}
                    movablePlaceholderIcon={faTicket}
                  />
                  <div className="mb-20"></div>
                </Fragment>
              )}

              <Checkbox
                checked={toMe}
                onChange={setToMe}
                label={t('mo.send_to_me', { email: user?.email })}
              />
            </div>

            <div className="modal-footer">
              {props.showThinVoucher && (
                <Button
                  className="btn btn-primary"
                  title={t(isPrinting ? 'mo.loading' : 'mo.download')}
                  onClick={() => onClickPrint()}
                  disabled={isPrinting}
                />
              )}

              <Button
                className="btn btn-primary"
                title={t('mo.send')}
                onClick={() => onClickSumit()}
              />
              <Button
                className="btn btn-default"
                title={t('close')}
                onClick={() => props.onClose()}
              />
            </div>
          </div>
        </div>
        , document.body)}
    </Fragment>
  );
};

export default withErrorBoundary(MoEmailModal, 'MoEmailModal');
